import React, { useCallback, useState, useEffect } from 'react';
import { Box, Button, Tooltip, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import PushPinIcon from '@mui/icons-material/PushPin';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import useTouchInteraction from '../../../hooks/useTouchInteraction';
import { shouldEnableOptionChain } from '../../../services/utilities';
import { setPinnedInstrumentsAction } from '../../../store/actions/watchlist';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { openPriceAlertModal } from '../../../store/actions/alerts';
import { openGTTModal, openBasketModal } from '../../../store/actions/orders';

// Helper function to check if instrument is an index
const isIndex = (instrument) => {
  return instrument?.index === true;
};

const TouchableIconButton = React.memo(({ onClick, children, title, color, sx }) => {
  const { handlers, touchStates } = useTouchInteraction(onClick, true);

  return (
    <Tooltip title={title}>
      <IconButton
        {...handlers}
        size="small"
        sx={{
          ...sx,
          backgroundColor: touchStates.isActive && !touchStates.isTouchMoved
            ? 'action.hover'
            : undefined,
          transition: 'all 0.2s ease',
          WebkitTapHighlightColor: 'transparent',
          width: '28px',
          height: '28px',
          color: color,
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
          }
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
});

const DesktopActions = ({
  instrument,
  isExpanded,
  onBuy,
  onSell,
  onMarketDepth,
  onDelete,
  onMore,
  onSaveToMarketwatch,
  isInMarketwatch,
  userSettings,
  showMarketDepth = true,
  onMenuStateChange,
  isDefaultWatchlist
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const pinnedInstruments = useSelector((state) => state.marketwatch.pins) || [];
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);

  const handleCreateAlertClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setMoreAnchorEl(null);
    // Snapshot the instrument so the modal isn't dependent on this row's
    // live socket-updated reference. Re-renders here can't touch the modal.
    dispatch(openPriceAlertModal({
      instrument: {
        token: instrument.token,
        exchange: instrument.exchange,
        trading_symbol: instrument.trading_symbol,
        display_name: instrument.display_name || instrument.trading_symbol,
        price_precision: instrument.price_precision,
        ltp: instrument.ltp,
      },
    }));
  }, [dispatch, instrument]);

  const handleCreateGTTClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setMoreAnchorEl(null);
    dispatch(openGTTModal({
      type: 'BUY',
      token: instrument.token,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      display_name: instrument.display_name || instrument.trading_symbol,
      ltp: instrument.ltp,
    }));
  }, [dispatch, instrument]);

  const handleAddToBasketClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setMoreAnchorEl(null);
    dispatch(openBasketModal({
      token: instrument.token,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      display_name: instrument.display_name || instrument.trading_symbol,
      price_precision: instrument.price_precision,
      ltp: instrument.ltp,
      is_tradable: instrument?.is_tradable ?? instrument?.tradable ?? true,
      lotsize: instrument.lotsize,
      tick_size: instrument.tick_size
    }));
  }, [dispatch, instrument]);

  useEffect(() => {
    if (onMenuStateChange) {
      onMenuStateChange(Boolean(moreAnchorEl));
    }
  }, [moreAnchorEl, onMenuStateChange]);

  const handleMoreClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setMoreAnchorEl(null);
  };

  const pinIndex = pinnedInstruments.findIndex(
    (pin) =>
      pin &&
      String(pin.token) === String(instrument.token) &&
      String(pin.exchange).toUpperCase().trim() === String(instrument.exchange).toUpperCase().trim()
  );
  const isPinned = pinIndex !== -1;
  const isLocked = pinIndex === 0 || pinIndex === 1;

  const handlePinToSlot = (targetIndex, event) => {
    event.preventDefault();
    event.stopPropagation();

    const filteredPins = pinnedInstruments.filter(
      (pin) =>
        !(pin &&
          String(pin.token) === String(instrument.token) &&
          String(pin.exchange).toUpperCase().trim() === String(instrument.exchange).toUpperCase().trim())
    );

    const newPinObj = {
      display_name: instrument.display_name || instrument.trading_symbol || "",
      exchange: instrument.exchange || "",
      is_index: instrument.is_index ?? false,
      is_tradable: instrument.is_tradable ?? true,
      token: instrument.token,
      trading_symbol: instrument.trading_symbol || "",
    };

    filteredPins[targetIndex] = newPinObj;

    dispatch(setPinnedInstrumentsAction(filteredPins))
      .then(() => {
        enqueueSnackbar(
          `Pinned ${instrument.display_name || instrument.trading_symbol} to Slot ${targetIndex + 1}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to pin instrument", { variant: "error" });
      });

    setMoreAnchorEl(null);
  };

  const handleUnpin = (pinIdx, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (pinIdx === 0 || pinIdx === 1) {
      enqueueSnackbar("The first two pinned instruments cannot be unpinned.", { variant: "warning" });
      setMoreAnchorEl(null);
      return;
    }

    const newPins = pinnedInstruments.filter((_, idx) => idx !== pinIdx);

    dispatch(setPinnedInstrumentsAction(newPins))
      .then(() => {
        enqueueSnackbar(
          `Unpinned ${instrument.display_name || instrument.trading_symbol}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to unpin instrument", { variant: "error" });
      });

    setMoreAnchorEl(null);
  };

  const handleOptionChainClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (instrument?.exchange && instrument?.token) {
      const precision = (instrument?.price_precision !== undefined && instrument?.price_precision !== null && instrument?.price_precision !== '')
        ? instrument.price_precision
        : 2;
      navigate(`/overview/tdv/${instrument.exchange}/${instrument.token}/${precision}`, {
        state: { defaultTab: 'chart' }
      });
    }
  }, [instrument?.exchange, instrument?.token, instrument?.price_precision, navigate]);

  const handleDelete = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(e);
  }, [onDelete]);

  const handleMarketDepth = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onMarketDepth(e);
  }, [onMarketDepth]);

  const isInstrumentIndex = isIndex(instrument);
  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        right: 8,
        top: isExpanded ? '20px' : '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        gap: 0.5,
        backgroundColor: 'background.paper',
        borderRadius: '20px',
        boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
        padding: '6px 10px',
        zIndex: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
          transform: 'translateY(-52%)',
        },
        pointerEvents: 'auto'
      }}
    >
      {!isInstrumentIndex && (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onBuy(instrument, e, userSettings);
          }}
          disabled={!instrument?.tradable}
          sx={{
            backgroundColor: !instrument?.tradable ? 'text.disabled' : 'success.main',
            '&:hover': {
              backgroundColor: !instrument?.tradable ? 'text.disabled' : 'success.dark',
              transform: !instrument?.tradable ? 'none' : 'translateY(-1px)',
              boxShadow: !instrument?.tradable ? 'none' : '0 2px 5px rgba(0,0,0,0.2)'
            },
            minWidth: '64px',
            height: '28px',
            fontSize: '0.8rem',
            fontWeight: 600,
            borderRadius: '14px',
            transition: 'all 0.2s ease',
            textTransform: 'none',
            pointerEvents: !instrument?.tradable ? 'none' : 'auto'
          }}
        >
          Buy
        </Button>
      )}

      {!isInstrumentIndex && (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onSell(instrument, e, userSettings);
          }}
          disabled={!instrument?.tradable}
          sx={{
            backgroundColor: !instrument?.tradable ? 'text.disabled' : 'error.main',
            '&:hover': {
              backgroundColor: !instrument?.tradable ? 'text.disabled' : 'error.dark',
              transform: !instrument?.tradable ? 'none' : 'translateY(-1px)',
              boxShadow: !instrument?.tradable ? 'none' : '0 2px 5px rgba(0,0,0,0.2)'
            },
            minWidth: '64px',
            height: '28px',
            fontSize: '0.8rem',
            fontWeight: 600,
            borderRadius: '14px',
            transition: 'all 0.2s ease',
            textTransform: 'none',
            pointerEvents: !instrument?.tradable ? 'none' : 'auto'
          }}
        >
          Sell
        </Button>
      )}

      {/* Hide chart option for CDS exchange */}
      <Box
        onClick={handleOptionChainClick}
        sx={{
          display: 'flex',
          pointerEvents: 'auto'
        }}
      >
        <TouchableIconButton
          onClick={handleOptionChainClick}
          title={shouldEnableOptionChain(instrument) ? "Option Chain + Charts" : "Charts"}
          color="primary.main"
        >
          <TimelineIcon fontSize="small" />
        </TouchableIconButton>
      </Box>

      {showMarketDepth && (
        <Box
          onClick={handleMarketDepth}
          sx={{
            display: 'flex',
            pointerEvents: 'auto'
          }}
        >
          <TouchableIconButton
            onClick={handleMarketDepth}
            title="Market Depth"
            color={isExpanded ? 'primary.main' : 'text.secondary'}
            sx={{
              backgroundColor: isExpanded ? 'action.selected' : 'transparent'
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </TouchableIconButton>
        </Box>
      )}

      {!isDefaultWatchlist && (
        <Box
          onClick={handleDelete}
          sx={{
            display: 'flex',
            pointerEvents: 'auto'
          }}
        >
          <TouchableIconButton
            onClick={handleDelete}
            title="Delete"
            color="error.main"
          >
            <DeleteIcon fontSize="small" />
          </TouchableIconButton>
        </Box>
      )}

      <Box
        onClick={handleMoreClick}
        sx={{
          display: 'flex',
          pointerEvents: 'auto'
        }}
      >
        <TouchableIconButton
          onClick={handleMoreClick}
          title="More Actions"
          color="text.secondary"
        >
          <MoreVertIcon fontSize="small" />
        </TouchableIconButton>
      </Box>

      <Menu
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl)}
        onClose={handleMoreClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: (theme) => ({
            background: theme.palette.background.header,
            color: theme.palette.text.header,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '0.5rem',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 4px 12px rgba(0,0,0,0.1)',
            mt: 0.5
          })
        }}
      >
        <MenuItem
          disableRipple
          sx={{
            py: 1,
            px: 2,
            fontSize: '0.875rem',
            cursor: 'default',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            '&:hover': { backgroundColor: 'transparent' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PushPinIcon fontSize="small" sx={{ color: 'primary.main', transform: 'rotate(45deg)' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Pin Instrument</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={pinIndex === 0 ? "Pinned to Slot 1" : "Pin to Slot 1"}>
              <span>
                <Button
                  size="small"
                  variant={pinIndex === 0 ? "contained" : "outlined"}
                  disabled={pinIndex === 0}
                  onClick={(e) => handlePinToSlot(0, e)}
                  sx={(theme) => ({
                    minWidth: '24px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    padding: 0,
                    fontSize: '0.75rem',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    color: pinIndex === 0 ? 'white' : theme.palette.text.primary,
                    '&:hover': {
                      borderColor: '#4facfe',
                      backgroundColor: 'rgba(79, 172, 254, 0.2)',
                    }
                  })}
                >
                  1
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={pinIndex === 1 ? "Pinned to Slot 2" : "Pin to Slot 2"}>
              <span>
                <Button
                  size="small"
                  variant={pinIndex === 1 ? "contained" : "outlined"}
                  disabled={pinIndex === 1}
                  onClick={(e) => handlePinToSlot(1, e)}
                  sx={(theme) => ({
                    minWidth: '24px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    padding: 0,
                    fontSize: '0.75rem',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    color: pinIndex === 1 ? 'white' : theme.palette.text.primary,
                    '&:hover': {
                      borderColor: '#4facfe',
                      backgroundColor: 'rgba(79, 172, 254, 0.2)',
                    }
                  })}
                >
                  2
                </Button>
              </span>
            </Tooltip>
          </Box>
        </MenuItem>

        {isPinned && !isLocked && (
          <MenuItem
            onClick={(e) => handleUnpin(pinIndex, e)}
            sx={{
              py: 1,
              px: 2,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PushPinIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Unpin Instrument</Typography>
          </MenuItem>
        )}

        <MenuItem
          onClick={handleCreateAlertClick}
          sx={{ py: 1, px: 2, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <NotificationsActiveOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Create Price Alert</Typography>
        </MenuItem>

        {!isInstrumentIndex && (
          <MenuItem
            onClick={handleCreateGTTClick}
            disabled={!instrument?.tradable}
            sx={{ py: 1, px: 2, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AccessTimeIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Create GTT</Typography>
          </MenuItem>
        )}

        {!isInstrumentIndex && (
          <MenuItem
            onClick={handleAddToBasketClick}
            disabled={!instrument?.tradable}
            sx={{ py: 1, px: 2, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ShoppingBasketIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Add to Basket</Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default DesktopActions; 